import { useState, useRef, useCallback } from 'react';
import { parseEpisodeTitle, formatEpisodeDate } from '../../utils/time';
import { HiPlay, HiXMark, HiQueueList, HiBars3 } from 'react-icons/hi2';
import type { RecentEpisode } from '../../types';

interface QueueProps {
  queue: RecentEpisode[];
  currentEpisodeId: number;
  fallbackImage: string;
  onRemove: (episodeId: number) => void;
  onPlay: (episodeId: number) => void;
  onClear: () => void;
  onReorder?: (newQueue: RecentEpisode[]) => void;
}

interface QueueItemProps {
  episode: RecentEpisode;
  fallbackImage: string;
  isCurrentEpisode: boolean;
  onRemove: () => void;
  onPlay: () => void;
  isDraggable?: boolean;
  isDragging?: boolean;
  isDragOver?: boolean;
  dragHandleProps?: {
    onPointerDown: (e: React.PointerEvent) => void;
  };
}

function QueueItem({ episode, fallbackImage, isCurrentEpisode, onRemove, onPlay, isDraggable, isDragging, isDragOver, dragHandleProps }: QueueItemProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const { name: episodeName } = parseEpisodeTitle(episode.episodetitle);
  const dateInfo = formatEpisodeDate(episode.datetime);

  return (
    <div
      className={`flex items-center gap-2 p-2 rounded-lg
                 transition-all duration-150 ease-out group
                 ${isCurrentEpisode
                   ? 'bg-[var(--color-primary)]/20 border border-[var(--color-primary)]/30'
                   : 'hover:bg-[var(--color-surface)]/60 border border-transparent'
                 }
                 ${isDragging ? 'opacity-30 scale-95' : ''}
                 ${isDragOver ? 'border-t-2 !border-t-[var(--color-primary)]' : ''}`}
    >
      {/* Drag handle - only for draggable items */}
      {isDraggable && dragHandleProps && (
        <button
          {...dragHandleProps}
          className="flex-shrink-0 p-1 cursor-grab active:cursor-grabbing
                     text-[var(--color-text-muted)]/50 hover:text-[var(--color-text-muted)]
                     transition-colors touch-none select-none"
          aria-label="Drag to reorder"
        >
          <HiBars3 size={14} />
        </button>
      )}

      {/* Thumbnail with play button */}
      <button
        onClick={onPlay}
        className="relative w-10 h-10 flex-shrink-0 rounded-md overflow-hidden bg-[var(--color-border)]
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
        aria-label={`Play ${episodeName}`}
      >
        <img
          src={episode.image || fallbackImage}
          alt=""
          loading="lazy"
          className={`w-full h-full object-cover transition-opacity duration-300
                     ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (target.src !== fallbackImage) {
              target.src = fallbackImage;
            }
            setImageLoaded(true);
          }}
        />
        {!isCurrentEpisode && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center
                          opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            <HiPlay size={16} className="text-white" />
          </div>
        )}
      </button>

      {/* Info - date prominent, show title secondary */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5">
          <span className={`text-xs font-semibold
                          ${isCurrentEpisode ? 'text-[var(--color-primary)]' : 'text-[var(--color-text)]'}`}>
            {dateInfo.primary}
          </span>
          <span className="text-[10px] text-[var(--color-text-muted)]">
            {dateInfo.secondary}
          </span>
        </div>
        <p className="text-[10px] text-[var(--color-text-muted)] truncate mt-0.5">
          {episode.showtitle} · {episodeName}
        </p>
      </div>

      {/* Remove button */}
      {!isCurrentEpisode && (
        <button
          onClick={onRemove}
          className="flex-shrink-0 p-1 rounded-full
                     opacity-0 group-hover:opacity-100
                     hover:bg-[var(--color-surface-elevated)]
                     transition-all duration-150
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
          aria-label="Remove from queue"
        >
          <HiXMark size={14} className="text-[var(--color-text-muted)]" />
        </button>
      )}

      {/* Now playing indicator */}
      {isCurrentEpisode && (
        <span className="flex-shrink-0 text-[10px] font-semibold text-[var(--color-primary)] uppercase">
          Now
        </span>
      )}
    </div>
  );
}

export function Queue({ queue, currentEpisodeId, fallbackImage, onRemove, onPlay, onClear, onReorder }: QueueProps) {
  const hasItems = queue.length > 0;
  const currentIndex = queue.findIndex(ep => ep.episodeid === currentEpisodeId);

  // Split queue into previous, current, and up next
  const previousEpisodes = currentIndex > 0 ? queue.slice(0, currentIndex) : [];
  const currentEpisode = currentIndex >= 0 ? queue[currentIndex] : null;
  const upNextEpisodes = currentIndex >= 0 ? queue.slice(currentIndex + 1) : queue;

  // Drag-and-drop state for "Up Next" reordering
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const dragStartY = useRef(0);
  const activePointerId = useRef<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const handleDragStart = useCallback((index: number, e: React.PointerEvent) => {
    if (!onReorder) return;
    activePointerId.current = e.pointerId;
    dragStartY.current = e.clientY;
    setDragIndex(index);
    setOverIndex(index);

    // Capture pointer for reliable tracking
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [onReorder]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (dragIndex === null || activePointerId.current !== e.pointerId) return;

    // Find which item the pointer is over based on Y position
    const listEl = listRef.current;
    if (!listEl) return;

    const items = Array.from(listEl.children) as HTMLElement[];
    let newOverIndex = dragIndex;

    for (let i = 0; i < items.length; i++) {
      const rect = items[i].getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      if (e.clientY < midY) {
        newOverIndex = i;
        break;
      }
      newOverIndex = i;
    }

    // Clamp to valid range
    newOverIndex = Math.max(0, Math.min(newOverIndex, upNextEpisodes.length - 1));
    setOverIndex(newOverIndex);
  }, [dragIndex, upNextEpisodes.length]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (dragIndex === null || activePointerId.current !== e.pointerId) return;

    // Apply reorder if position changed
    if (overIndex !== null && overIndex !== dragIndex && onReorder) {
      const newUpNext = [...upNextEpisodes];
      const [moved] = newUpNext.splice(dragIndex, 1);
      newUpNext.splice(overIndex, 0, moved);

      // Reconstruct full queue
      const newQueue = [
        ...previousEpisodes,
        ...(currentEpisode ? [currentEpisode] : []),
        ...newUpNext,
      ];
      onReorder(newQueue);
    }

    // Reset drag state
    setDragIndex(null);
    setOverIndex(null);
    activePointerId.current = null;

    // Release pointer capture
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch { /* ignore if already released */ }
  }, [dragIndex, overIndex, onReorder, upNextEpisodes, previousEpisodes, currentEpisode]);

  // Build the visual order for "Up Next" during drag
  const getUpNextDisplayOrder = () => {
    if (dragIndex === null || overIndex === null || dragIndex === overIndex) {
      return upNextEpisodes.map((ep, i) => ({ ep, originalIndex: i }));
    }
    // Simulate the reorder for visual preview
    const items = upNextEpisodes.map((ep, i) => ({ ep, originalIndex: i }));
    const [moved] = items.splice(dragIndex, 1);
    items.splice(overIndex, 0, moved);
    return items;
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2
                      border-b border-[var(--color-border)]/50">
        <div className="flex items-center gap-2">
          <HiQueueList size={16} className="text-[var(--color-text-secondary)]" />
          <h3 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
            Queue ({queue.length})
          </h3>
        </div>
        {hasItems && (
          <button
            onClick={onClear}
            className="text-[10px] font-medium text-[var(--color-text-muted)]
                       hover:text-[var(--color-text)] transition-colors
                       focus:outline-none focus-visible:underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* Queue items */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {hasItems ? (
          <div className="space-y-3">
            {/* Now Playing - always at top */}
            {currentEpisode && (
              <div>
                <p className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase px-2 mb-1">
                  Now Playing
                </p>
                <QueueItem
                  episode={currentEpisode}
                  fallbackImage={fallbackImage}
                  isCurrentEpisode={true}
                  onRemove={() => onRemove(currentEpisode.episodeid)}
                  onPlay={() => onPlay(currentEpisode.episodeid)}
                />
              </div>
            )}

            {/* Up Next - reorderable */}
            {upNextEpisodes.length > 0 && (
              <div>
                <p className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase px-2 mb-1">
                  Up Next ({upNextEpisodes.length})
                </p>
                <div
                  ref={listRef}
                  className="space-y-1"
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                >
                  {getUpNextDisplayOrder().map(({ ep, originalIndex }) => (
                    <div
                      key={ep.episodeid}
                      ref={(el) => {
                        if (el) itemRefs.current.set(originalIndex, el);
                        else itemRefs.current.delete(originalIndex);
                      }}
                    >
                      <QueueItem
                        episode={ep}
                        fallbackImage={fallbackImage}
                        isCurrentEpisode={false}
                        onRemove={() => onRemove(ep.episodeid)}
                        onPlay={() => onPlay(ep.episodeid)}
                        isDraggable={!!onReorder}
                        isDragging={dragIndex === originalIndex}
                        isDragOver={overIndex !== null && overIndex !== dragIndex && originalIndex === overIndex}
                        dragHandleProps={onReorder ? {
                          onPointerDown: (e) => handleDragStart(originalIndex, e),
                        } : undefined}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Previous episodes - at bottom, reversed so most recent is first */}
            {previousEpisodes.length > 0 && (
              <div>
                <p className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase px-2 mb-1">
                  Previous ({previousEpisodes.length})
                </p>
                <div className="space-y-1 opacity-60">
                  {[...previousEpisodes].reverse().map((ep) => (
                    <QueueItem
                      key={ep.episodeid}
                      episode={ep}
                      fallbackImage={fallbackImage}
                      isCurrentEpisode={false}
                      onRemove={() => onRemove(ep.episodeid)}
                      onPlay={() => onPlay(ep.episodeid)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <HiQueueList size={32} className="text-[var(--color-text-muted)]/50 mb-2" />
            <p className="text-xs text-[var(--color-text-muted)]">
              Your queue is empty
            </p>
            <p className="text-[10px] text-[var(--color-text-muted)]/70 mt-1">
              Add episodes from the right panel
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
